<template>
  <div v-if="showModuloForm === false">
    <MarketingModules></MarketingModules>
  </div>
  <div class="page perfis-wizard-page" v-else-if="showModuloForm === true">
    <Panel lineColor="var(--primary-color)">
      <PerfisUsuarioWizardStepper
        :steps="wizardSteps"
        :current-step="currentStep"
        :completed-steps="completedSteps"
      />
      <v-divider class="mb-8"></v-divider>
      <div v-if="loading" class="perfis-loading">
        <v-progress-circular indeterminate color="primary" />
      </div>

      <template v-else>
        <PerfisUsuarioStepDados
          v-if="currentStep === 1"
          ref="stepDados"
          :wizard-state="state"
          @update-fields="onUpdateDados"
          @dirty="markDirty"
        />

        <PerfisUsuarioStepPlanos
          v-else-if="currentStep === 2"
          ref="stepPlanos"
          :idfornecedor="String(getFornecedorId || '')"
          :idperfil-edicao="state.idperfil"
          :idformapagtos="state.idformapagtos"
          @update-planos="onUpdatePlanos"
          @vinculos-alterados="onVinculosAlterados"
          @dirty="markDirty"
        />
      </template>

      <div class="wizard-footer mt-6">
        <FormButton
          text="Cancelar"
          type="perigo"
          before-icon="fas fa-times"
          :disabled="saving"
          @click="onCancel"
        />
        <div class="wizard-footer__actions">
          <FormButton
            text="Voltar"
            type="secundario"
            before-icon="fas fa-arrow-left"
            :disabled="currentStep <= 1 || saving"
            @click="onBack"
          />
          <FormButton
            :text="continueLabel"
            type="primario"
            :after-icon="
              currentStep < 2 ? 'fas fa-chevron-right' : 'fas fa-check'
            "
            :loading="saving"
            @click="onContinue"
          />
        </div>
      </div>
    </Panel>
  </div>
</template>

<script>
import Panel from "@/components/Panel";
import FormButton from "@/components/form/FormButton";
import MarketingModules from "@/components/MarketingModules";
import confirmAlert from "@/mixins/confirmAlert";
import { mapGetters } from "vuex";
import { ModulesEnum } from "@/store/actions/modulesEnum";
import {
  PERFIL_USUARIO_GET_BY_ID,
  PERFIL_USUARIO_CREATE,
  PERFIL_USUARIO_UPDATE,
  PERFIL_USUARIO_REMOVE
} from "@/store/actions/perfilUsuario";
import PerfisUsuarioWizardStepper from "./PerfisUsuarioWizardStepper";
import PerfisUsuarioStepDados from "./PerfisUsuarioStepDados";
import PerfisUsuarioStepPlanos from "./PerfisUsuarioStepPlanos";
import {
  buildCreatePayload,
  buildUpdatePayload,
  defaultWizardState,
  snapshotWizardBaseline
} from "./utils";

export default {
  name: "PerfisUsuarioWizard",
  components: {
    Panel,
    FormButton,
    MarketingModules,
    PerfisUsuarioWizardStepper,
    PerfisUsuarioStepDados,
    PerfisUsuarioStepPlanos
  },
  mixins: [confirmAlert],
  data: () => ({
    modulosForm: [ModulesEnum.LIFEGROWTH],
    currentStep: 1,
    completedSteps: [],
    loading: false,
    saving: false,
    dirty: false,
    draftCreatedInSession: false,
    baselineSnapshot: null,
    state: defaultWizardState(null)
  }),
  computed: {
    ...mapGetters(["getFornecedorId", "moduloFornecedorAtivo"]),
    showModuloForm() {
      return this.moduloFornecedorAtivo(this.modulosForm);
    },
    isEdit() {
      return (
        this.$route.name === "PerfisUsuarioEditar" &&
        !!this.$route.params.idperfil
      );
    },
    continueLabel() {
      return this.currentStep < 2 ? "Continuar" : "Salvar perfil";
    },
    wizardSteps() {
      const p = (this.state.idformapagtos || []).length;
      return [
        { id: 1, label: "Dados do perfil" },
        {
          id: 2,
          label: "Planos permitidos",
          subtitle: p ? `${p} selecionado(s)` : "Sem restrição"
        }
      ];
    }
  },
  watch: {
    showModuloForm(val) {
      if (val) this.bootstrap();
    },
    getFornecedorId: {
      immediate: true,
      handler(id) {
        if (id && !this.state.idfornecedor) {
          this.state.idfornecedor = id;
        }
      }
    },
    "$route.params.idperfil"() {
      this.bootstrap();
    }
  },
  created() {
    this.bootstrap();
  },
  methods: {
    markDirty() {
      this.dirty = true;
    },
    onUpdateDados(fields) {
      Object.assign(this.state, fields);
    },
    onUpdatePlanos(ids) {
      this.state.idformapagtos = ids;
    },
    applyPerfilDetail(d) {
      this.state = {
        idperfil: d.idperfil,
        idfornecedor: d.idfornecedor || this.getFornecedorId,
        nome: d.nome || "",
        descricao: d.descricao || "",
        ativo: d.ativo !== false,
        idformapagtos: d.idformapagtos || []
      };
    },
    async refreshPerfilFromServer() {
      if (!this.state.idperfil) return;
      const data = await this.$store.dispatch(
        PERFIL_USUARIO_GET_BY_ID,
        this.state.idperfil
      );
      const d = data?.data || {};
      this.state.idformapagtos = d.idformapagtos || [];
    },
    async onVinculosAlterados() {
      await this.refreshPerfilFromServer();
      this.markDirty();
      this.$nextTick(() => {
        const step = this.$refs.stepPlanos;
        if (step && step.reloadTable) {
          step.reloadTable();
        }
      });
    },
    async bootstrap() {
      if (!this.showModuloForm || !this.getFornecedorId) return;
      this.state.idfornecedor = this.getFornecedorId;
      const routePerfilId = this.$route.params.idperfil;
      if (!routePerfilId) {
        this.draftCreatedInSession = false;
        this.baselineSnapshot = null;
        this.dirty = false;
        this.state = defaultWizardState(this.getFornecedorId);
        return;
      }
      if (this.draftCreatedInSession && routePerfilId === this.state.idperfil) {
        return;
      }
      this.loading = true;
      try {
        const data = await this.$store.dispatch(
          PERFIL_USUARIO_GET_BY_ID,
          routePerfilId
        );
        const d = data?.data || {};
        this.applyPerfilDetail(d);
        this.baselineSnapshot = snapshotWizardBaseline(this.state);
        this.draftCreatedInSession = false;
        this.dirty = false;
      } catch (err) {
        this.$router.push("/fornecedor/configuracao/perfis-usuarios");
      } finally {
        this.loading = false;
      }
    },
    validateStep1() {
      const step = this.$refs.stepDados;
      if (step && step.syncToState) step.syncToState();
      if (step && step.validate) {
        return step.validate();
      }
      return !!(this.state.nome || "").trim();
    },
    onBack() {
      if (this.currentStep > 1) {
        this.currentStep -= 1;
      }
    },
    async onContinue() {
      if (this.currentStep === 1) {
        if (!this.validateStep1()) return;
        if (!this.state.idperfil) {
          this.saving = true;
          try {
            const created = await this.$store.dispatch(
              PERFIL_USUARIO_CREATE,
              buildCreatePayload(this.state, this.getFornecedorId)
            );
            const d = created?.data || created || {};
            this.applyPerfilDetail(d);
            this.baselineSnapshot = snapshotWizardBaseline(this.state);
            this.draftCreatedInSession = true;
            await this.$router.replace(
              `/fornecedor/configuracao/perfis-usuarios/${this.state.idperfil}`
            );
          } catch (err) {
            return;
          } finally {
            this.saving = false;
          }
        }
        if (!this.completedSteps.includes(1)) {
          this.completedSteps.push(1);
        }
        this.currentStep = 2;
        return;
      }
      await this.save();
    },
    async save() {
      if (!this.validateStep1()) {
        this.currentStep = 1;
        return;
      }
      if (!this.state.idperfil) {
        this.currentStep = 1;
        return;
      }
      this.saving = true;
      try {
        await this.$store.dispatch(PERFIL_USUARIO_UPDATE, {
          idperfil: this.state.idperfil,
          payload: buildUpdatePayload(this.state)
        });
        this.dirty = false;
        this.draftCreatedInSession = false;
        this.$router.push("/fornecedor/configuracao/perfis-usuarios");
      } catch (err) {
        /* ERRORS_SHOW na action */
      } finally {
        this.saving = false;
      }
    },
    async discardUnsavedAndLeave() {
      if (this.draftCreatedInSession && this.state.idperfil) {
        await this.$store.dispatch(PERFIL_USUARIO_REMOVE, this.state.idperfil);
      } else if (this.baselineSnapshot && this.state.idperfil && this.dirty) {
        await this.$store.dispatch(PERFIL_USUARIO_UPDATE, {
          idperfil: this.state.idperfil,
          payload: this.baselineSnapshot
        });
      }
    },
    onCancel() {
      const go = () =>
        this.$router.push("/fornecedor/configuracao/perfis-usuarios");
      if (this.draftCreatedInSession && this.state.idperfil && !this.dirty) {
        return this.discardUnsavedAndLeave()
          .catch(() => {
            /* ERRORS_SHOW na action */
          })
          .finally(go);
      }
      if (!this.dirty) return go();
      this.confirmSwal(
        {
          title: "Descartar alterações?",
          text: "As informações não salvas serão perdidas."
        },
        async () => {
          try {
            await this.discardUnsavedAndLeave();
          } catch (err) {
            /* ERRORS_SHOW na action */
          }
          go();
        }
      );
    }
  }
};
</script>

<style scoped>
.wizard-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}
.wizard-footer__actions {
  display: flex;
  gap: 8px;
}
.perfis-loading {
  display: flex;
  justify-content: center;
  padding: 32px;
}
</style>
